/**
 * AdminLogsPage Component
 * Shared date range for Activity Logs + Analytics; list filters stay on Activity only.
 */

import React, { useState, useCallback, useMemo } from 'react';
import {
  RefreshCw,
  List,
  BarChart2,
} from 'lucide-react';
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  Button,
  SpinnerLoader,
  Toastify,
  DatePickerDateRange,
} from 'lift-ui';
import { useAdminLogs } from '../../hooks/useAdminLogs';
import {
  getDefaultDateRange,
  normalizeDateRange,
} from '../../utils';
import { LogsFilterBar } from '../LogsFilterBar';
import { LogsStatsCards } from '../LogsStatsCards';
import { EnhancedLogsTable } from '../EnhancedLogsTable';
import { AnalyticsDashboard } from '../AnalyticsDashboard';

type ViewTab = 'activity' | 'analytics';

export function AdminLogsPage() {
  const [activeTab, setActiveTab] = useState<ViewTab>('activity');

  const {
    logs,
    allLogs,
    stats,
    users,
    filters,
    isLoading,
    isRefreshing,
    isLoadingMore,
    error,
    hasMore,
    setFilters,
    refresh,
    loadMore,
    hasActiveFilters,
  } = useAdminLogs({ pageSize: 25 });

  const defaultRange = useMemo(() => getDefaultDateRange(7), []);

  const resolvedStart =
    filters.dateRange.startDate ?? defaultRange.startDate;
  const resolvedEnd = filters.dateRange.endDate ?? defaultRange.endDate;

  const handleRefresh = useCallback(async () => {
    try {
      await refresh();
      Toastify('success', 'Logs refreshed successfully');
    } catch {
      Toastify('error', 'Failed to refresh logs');
    }
  }, [refresh]);

  const handleDateApply = useCallback(
    (start: Date, end: Date) => {
      setFilters({ dateRange: normalizeDateRange(start, end) });
    },
    [setFilters]
  );

  /** Clear Activity list filters only — keep the shared date range. */
  const handleClearListFilters = useCallback(() => {
    setFilters({
      searchText: '',
      severity: [],
      categories: [],
      users: [],
      showOnlyErrors: false,
    });
  }, [setFilters]);

  return (
    <Tabs
      value={activeTab}
      onValueChange={(value) => setActiveTab(value as ViewTab)}
      className="flex flex-col h-full min-w-0 bg-white"
    >
      {/* Shared chrome: tabs | KPIs | date range | refresh */}
      <div className="border-b bg-white px-4 py-2.5 shrink-0">
        <div className="flex items-center gap-3 min-w-0">
          <TabsList className="h-10 p-1 bg-gray-100 rounded-lg shrink-0">
            <TabsTrigger
              value="activity"
              className="gap-2 px-4 h-8 rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm"
            >
              <List className="h-4 w-4" />
              Activity Logs
            </TabsTrigger>
            <TabsTrigger
              value="analytics"
              className="gap-2 px-4 h-8 rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm"
            >
              <BarChart2 className="h-4 w-4" />
              Analytics
            </TabsTrigger>
          </TabsList>

          {activeTab === 'activity' ? (
            <LogsStatsCards
              stats={stats}
              isLoading={isLoading && logs.length === 0}
            />
          ) : (
            <div className="flex-1 min-w-0 text-sm text-[#585755] truncate px-1">
              Metrics use the same date range as Activity Logs
            </div>
          )}

          <div className="shrink-0" title="Shared date range for Activity Logs and Analytics">
            <DatePickerDateRange
              initialStartDate={resolvedStart}
              initialEndDate={resolvedEnd}
              onApply={handleDateApply}
              fiscal={false}
              yearType="calendar"
              defaultDatePreset="Last 7 days"
            />
          </div>

          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="flex items-center gap-2 h-9 shrink-0"
          >
            {isRefreshing ? (
              <SpinnerLoader className="h-4 w-4" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            Refresh
          </Button>
        </div>
      </div>

      <div className="flex-1 overflow-hidden flex flex-col min-w-0 px-4 py-3">
        <TabsContent
          value="activity"
          className="flex-1 flex flex-col min-h-0 min-w-0 mt-0 gap-3 data-[state=inactive]:hidden"
        >
          <LogsFilterBar
            filters={filters}
            users={users}
            onFilterChange={setFilters}
            onReset={handleClearListFilters}
            hasActiveFilters={hasActiveFilters}
            totalResults={logs.length}
            hasMore={hasMore}
          />

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800 shrink-0">
              <strong>Error:</strong> {error}
            </div>
          )}

          <div className="flex-1 min-h-0 min-w-0">
            <EnhancedLogsTable
              logs={logs}
              searchText={filters.searchText}
              isLoading={isLoading}
              isLoadingMore={isLoadingMore}
              onLoadMore={loadMore}
              hasMore={hasMore}
            />
          </div>
        </TabsContent>

        <TabsContent
          value="analytics"
          className="flex-1 min-w-0 overflow-y-auto mt-0 data-[state=inactive]:hidden"
        >
          <AnalyticsDashboard
            logs={allLogs}
            dateRange={filters.dateRange}
            isLoading={isLoading || isRefreshing}
          />
        </TabsContent>
      </div>
    </Tabs>
  );
}
